/**
 * Logger estruturado para o sistema ZDAT
 * Centraliza logging com contexto, severidade e metadados
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogEntry {
  level: LogLevel;
  module: string;
  message: string;
  data?: Record<string, unknown>;
  timestamp: string;
}

const formatEntry = (entry: LogEntry): string => {
  const prefix = `[${entry.timestamp}] [${entry.level.toUpperCase()}] [${entry.module}]`;
  return `${prefix} ${entry.message}`;
};

const createEntry = (
  level: LogLevel,
  module: string,
  message: string,
  data?: Record<string, unknown>
): LogEntry => ({
  level,
  module,
  message,
  data,
  timestamp: new Date().toISOString(),
});

const shouldLog = (level: LogLevel): boolean => {
  const levels: LogLevel[] = ['debug', 'info', 'warn', 'error'];
  const minLevel = import.meta.env.DEV ? 'debug' : 'warn';
  return levels.indexOf(level) >= levels.indexOf(minLevel);
};

/**
 * Cria um logger com módulo pré-definido
 * @example
 * const log = createLogger('useDebts');
 * log.error('Falha ao buscar dívidas', { error: err.message });
 */
export const createLogger = (module: string) => {
  const log = (level: LogLevel, message: string, data?: Record<string, unknown>) => {
    if (!shouldLog(level)) return;

    const entry = createEntry(level, module, message, data);
    const formatted = formatEntry(entry);

    switch (level) {
      case 'debug':
        console.debug(formatted, data ?? '');
        break;
      case 'info':
        console.info(formatted, data ?? '');
        break;
      case 'warn':
        console.warn(formatted, data ?? '');
        break;
      case 'error':
        console.error(formatted, data ?? '');
        break;
    }
  };

  return {
    debug: (message: string, data?: Record<string, unknown>) => log('debug', message, data),
    info: (message: string, data?: Record<string, unknown>) => log('info', message, data),
    warn: (message: string, data?: Record<string, unknown>) => log('warn', message, data),
    error: (message: string, data?: Record<string, unknown>) => log('error', message, data),
  };
};

/** Logger padrão para uso rápido */
export const logger = createLogger('app');
